import React, { useState } from 'react';
import { Box, Typography, Alert, Button, Chip } from '@mui/material';
import { Lock as LockIcon, LockOpen as LockOpenIcon, Description as DescriptionIcon } from '@mui/icons-material';
import type { OsaResponse } from '../../api/JsdhNotifikaceService/generated/models/OsaResponse';
import { useZamknoutVlakno, useOdemknoutVlakno } from '../../api/JsdhNotifikaceService/service/ZasahMutations';
import { useDemoStatusQuery } from '../../api/JsdhNotifikaceService/service/FeedbackMutations';
import OsaRadek from './OsaRadek';
import PodkladDialog from './PodkladDialog';

interface CasovaOsaProps {
    poplachId: number;
    /** Presne to, co vraci `GET /api/poplach/{id}/osa` (Task 0) - komponenta si nic nedopocitava. */
    data: OsaResponse;
    /** Id nejrychlejsich odpovedi (ze statistik, ne z osy) - poradi urcuje index v poli. */
    nejrychlejsiIds?: number[];
    isAdmin?: boolean;
}

/**
 * Casova osa prubehu zasahu (HO-151 Task 1). Nahrazuje dnesni `VerticalTimeline`
 * v sekci "Prubeh udalosti", kdyz je zaznam zasahu (HO-148) pro jednotku
 * zapnuty - viz gating v `DetailUdalostiPage`.
 *
 * Osa NIKDY nerika "poplach ukoncen" - system nezna cas skutecneho ukonceni
 * zasahu. Kdyz `chybiCasDeaktivace` (starsi udalost pred migraci 113), rekneme
 * to nahlas misto zamlceni.
 *
 * HO-151 Task 3: tlacitko "Vygenerovat podklad" otevira `PodkladDialog` (funguje
 * bez ohledu na stav zamku - podklad se sklada i z polozek mimo vlakno). Zamek
 * jde ovladat jen kdyz vlakno existuje (`zamek !== null`) a NE v demo rezimu
 * (`useDemoStatusQuery`) - demo dovoli podklad prohlizet, ale ne mutovat stav.
 */
const CasovaOsa: React.FC<CasovaOsaProps> = ({ poplachId, data, nejrychlejsiIds = [], isAdmin = true }) => {
    const { polozky, chybiCasDeaktivace, zamek } = data;
    const zamknoutVlakno = useZamknoutVlakno(poplachId);
    const odemknoutVlakno = useOdemknoutVlakno(poplachId);
    const { data: demoStatus } = useDemoStatusQuery();
    const demoEnabled = demoStatus?.demoEnabled === true;
    const [podkladOtevren, setPodkladOtevren] = useState(false);

    const serazene = [...polozky].sort((a, b) => new Date(a.cas).getTime() - new Date(b.cas).getTime());
    const zamceno = zamek === 'UZAMCENO';

    const poradiNejrychlejsi = (entitaId: number | null): number => {
        if (entitaId === null) return 0;
        const index = nejrychlejsiIds.indexOf(entitaId);
        return index >= 0 ? index + 1 : 0;
    };

    return (
        <Box>
            {chybiCasDeaktivace && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Čas deaktivace u této události není zaznamenaný.
                </Alert>
            )}

            <Box mb={2}>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DescriptionIcon />}
                    disabled={!isAdmin}
                    onClick={() => setPodkladOtevren(true)}
                >
                    Vygenerovat podklad
                </Button>
            </Box>

            {podkladOtevren && (
                <PodkladDialog poplachId={poplachId} onClose={() => setPodkladOtevren(false)} />
            )}

            {zamek && (
                <Box mb={2}>
                    <Box display="flex" alignItems="center" gap={1}>
                        {zamek === 'OTEVRENO' ? (
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<LockIcon />}
                                disabled={!isAdmin || demoEnabled || zamknoutVlakno.isPending}
                                onClick={() => zamknoutVlakno.mutate()}
                            >
                                Zásah zdokumentován
                            </Button>
                        ) : (
                            <>
                                <Chip icon={<LockIcon />} label="Vlákno uzamčeno" size="small" />
                                <Button
                                    variant="outlined"
                                    size="small"
                                    color="secondary"
                                    startIcon={<LockOpenIcon />}
                                    disabled={!isAdmin || demoEnabled || odemknoutVlakno.isPending}
                                    onClick={() => odemknoutVlakno.mutate()}
                                >
                                    Odemknout vlákno
                                </Button>
                            </>
                        )}
                    </Box>
                    {zamceno && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Označování do zprávy je jen ke čtení - pro úpravu odemkněte vlákno.
                        </Typography>
                    )}
                </Box>
            )}

            <Box sx={{ py: 1 }}>
                {serazene.map((polozka, index) => (
                    <OsaRadek
                        key={`${polozka.druh}-${polozka.entitaId ?? 'x'}-${index}`}
                        poplachId={poplachId}
                        polozka={polozka}
                        poradiNejrychlejsi={poradiNejrychlejsi(polozka.entitaId)}
                        isAdmin={isAdmin}
                        zamceno={zamceno}
                        isLast={index === serazene.length - 1}
                    />
                ))}

                {serazene.length === 0 && (
                    <Typography color="text.secondary">
                        Zatím žádné události.
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

export default CasovaOsa;
